import { Box } from '@mui/material';
import React from 'react';
import { HeroImageType } from '../../../models/ContentManagementInterface';
import BackendService from '../../../services/admin/BackendService';
import { newContentId } from '../../../services/TemplateUtils';
import HeroImageCropControls from './HeroImageCropControls';
import HeroImageDropZone from './HeroImageDropZone';
import InlineText from './InlineText';
import type { ImageCardsCropMeta } from './useImageCardsSplit';
import { useHeroImageCropAdjust } from './useHeroImageCropAdjust';
import { useHeroImageUpload } from './useHeroImageUpload';

interface HeroImageBlockEditorProps {
  block: HeroImageType;
  onChange: (props: HeroImageType['properties']) => void;
  modelId: string;
  modelType: string;
  backendService: BackendService;
  /** Crop metadata restored on load, so "Adjust crop" is offered again. */
  primeCardsAdjust?: ImageCardsCropMeta;
  onPrimeCardsAdjustConsumed?: () => void;
}

const altTextSx: React.CSSProperties = { fontSize: '0.875rem', color: '#5f6b66' };

/**
 * The hero image, set or replaced in place, with its alt text underneath for
 * screen readers. One cut out of a pasted screenshot can have its crop adjusted.
 */
export default function HeroImageBlockEditor({
  block,
  onChange,
  modelId,
  modelType,
  backendService,
  primeCardsAdjust,
  onPrimeCardsAdjustConsumed,
}: HeroImageBlockEditorProps) {
  const { imageUrl, altText } = block.properties;
  const crop = useHeroImageCropAdjust({
    blockId: block.id,
    modelId,
    modelType,
    backendService,
    primed: primeCardsAdjust,
    onPrimedConsumed: onPrimeCardsAdjustConsumed,
    onRecropped: (recropped) => onChange({ ...block.properties, imageUrl: recropped }),
  });
  const { uploading, uploadFile } = useHeroImageUpload({
    modelId,
    modelType,
    backendService,
    onUploaded: (uploaded) => {
      crop.forget();
      onChange({ ...block.properties, imageUrl: uploaded });
    },
  });

  return (
    <Box>
      <HeroImageDropZone
        imageUrl={imageUrl}
        altText={altText?.content}
        uploading={uploading || crop.busy}
        onFile={(file) => void uploadFile(file)}
      />
      <HeroImageCropControls crop={crop} label={altText?.content || 'Hero image'} />
      <Box sx={{ mt: 1 }}>
        <InlineText
          value={altText?.content ?? ''}
          unset={!altText?.content?.trim()}
          onChange={(value) =>
            onChange({
              ...block.properties,
              altText: { id: altText?.id ?? newContentId(), content: value },
            })
          }
          placeholder="Describe the image for screen readers (alt text)"
          ariaLabel="Hero image alt text"
          sx={altTextSx}
        />
      </Box>
    </Box>
  );
}
